/**
 * HOF-512 — what each Deckungsbeitrag line IS, one block per line, in the drawer.
 *
 * „Wie die Leistung gerechnet ist" existed for exactly one line and was printed under every
 * one of them: open Saatgut/Pflanzen and the drawer explained the Leistung. A farmer reading
 * a per-line panel takes the sentence under it as a statement about the line they opened, so
 * a fixed Leistung note beside a Saatgut number is a false claim about where that number came
 * from — the same defect class as pointing a `↗` at a Preisspiegel cell that did not produce
 * the price.
 *
 * **The source is the customer's own agreed cost model**, not this app's implementation:
 * `kb:product/customers/wetterauer-fruechtchen/deckungsbeitrag-model.md`, Part II §2 (the
 * formula set, layers A/B/C and the Absatz side) and §3 (how each line travels from document
 * to €/kg — the document, the join key and the allocation basis, per line). That is the
 * agreement Tanja and Max reviewed, so it is the thing a farmer is entitled to read back;
 * describing what the code happens to do today would document an implementation, and would
 * drift the moment a line's source changes.
 *
 * **A pending line describes its FORMULA, not its absence.** `PENDING_LINES` already carries
 * one sentence each on why there is no figure yet („wird noch nicht je Kultur erfasst"), and
 * that sentence answers a different question than this block: the flag says why the cell is
 * empty, this says what will stand in it. Both are true at once and neither replaces the
 * other — so the pending lines are described here in the future tense, and the two texts are
 * deliberately not merged.
 *
 * Copy discipline, inherited from `leistungMark`: one claim per sentence, no percentages
 * where a count is the reader's next move, and never a claim the fact cannot support. Where
 * the model itself is unsettled the text says so rather than picking a side — the KFZ split
 * (§II.4) and the Pacht allocation (§II.5) are open questions with the customer, and a
 * confident sentence over an open question is how a UI teaches a farmer something false.
 */

/** The heading + body of one line's „wie gerechnet" block. */
export interface LineBasis {
  /** „Wie die Leistung gerechnet ist" — written out per line rather than templated, because
   *  German agreement (ist/sind/werden) does not survive a template over these labels. */
  heading: string;
  /** One claim per paragraph. Rendered in order. */
  body: string[];
}

/**
 * The KB's layer letter, carried so the block can say which block of the model a line sits
 * in. Layer A varies with the harvest, B with the area, C is annualised investment and D is
 * the Absatz side — the distinction the whole model is built on („Four cost layers, not
 * two", Part II §1), and the one a farmer needs in order to read two lines against each
 * other at all.
 */
const LAYER_NOTE: Record<string, string> = {
  A: 'Ertragsabhängig: die Kosten steigen mit jedem gepflückten Kilo.',
  B: 'Ertragsunabhängig: die Kosten fallen je Fläche an, egal wie viel geerntet wird.',
  C: 'Investition: einmalige Anschaffungen, auf ihre Nutzungsdauer verteilt.',
  D: 'Absatz: die Kosten hängen daran, WIE verkauft wird, nicht daran, wie angebaut wird.',
};

const basis = (heading: string, layer: keyof typeof LAYER_NOTE | null, ...body: string[]): LineBasis => ({
  heading,
  body: layer ? [...body, LAYER_NOTE[layer]] : body,
});

/**
 * Line key → its block. Keys are the cascade keys (`costLines`) and the `pending:` row ids
 * from `layers.ts`, so this map is addressed by exactly what the organism selects.
 */
export const LINE_BASIS: Record<string, LineBasis> = {
  leistung: basis(
    'Wie die Leistung gerechnet ist',
    null,
    'Erfasste Menge × gewichteter Monatsdurchschnittspreis aus deinem Preisspiegel — nicht das, ' +
      'was für diese Kultur abgerechnet wurde.',
  ),

  // KB §I.3 + §II.3: Angebot, not invoice — the single most consequential line-level decision
  // in the model, and the one a farmer is most likely to challenge, so it says WHY.
  saatgut: basis(
    'Wie Saatgut/Pflanzen gerechnet ist',
    'B',
    'Pflanzbestand × Preis je Pflanze, verteilt auf die Nutzungsdauer der Anlage.',
    'Der Preis kommt aus dem Angebot des Lieferanten, nicht aus den Rechnungen: Pflanzenrechnungen ' +
      'sind Anzahlungen, die über den Jahreswechsel laufen — im Herbst wird für die Pflanzen des ' +
      'übernächsten Jahres angezahlt, und das Rechnungsdatum sagt deshalb nichts über die Saison.',
  ),

  // KB §II.3: invoice → product name ↔ the MyFarm application record, field-direct.
  duenger_psm: basis(
    'Wie Dünger/PSM gerechnet ist',
    'B',
    'Ausgebrachte Menge je Anwendung aus deiner Schlagkartei × Preis je Einheit aus dem Preisspiegel.',
    'Die Anwendungen kommen aus MyFarm24 und sind dem Schlag direkt zugeordnet — es wird nichts ' +
      'über die Fläche verteilt, was nicht auf dieser Fläche ausgebracht wurde.',
  ),

  // KB §II.2 Layer B: Substrat, Vlies, Wasser, Strom. §II.3 gives the metered/farm-wide split.
  // 2026-10-06 — Substrat is no longer read off invoices: the engine IMPUTES it from the
  // running metres (Ludger's Stellage rule) for a substrate culture a rule names, and charges
  // none anywhere else. The sentences are conditional so they stay true of every cycle — one
  // with a Substrat row and one without. The rule set is config (deckungsbeitrag-parameters
  // § substrat: Erdbeeren × Stellage fleet-wide, Erdbeeren × Tunnel per tenant overlay).
  sonstige: basis(
    'Wie sonstige Direktkosten gerechnet sind',
    'B',
    'Vlies, Wasser und Strom — Menge × Preis aus den Rechnungen.',
    'Wo ein Zähler auf dem Schlag liegt, wird direkt zugeordnet; wo keiner liegt, wird der ' +
      'Betriebswert über die Hektar verteilt.',
    'Substrat wird aus den Laufmetern gerechnet — nur für Kultur und Anbausystem, für die ein ' +
      'Richtwert hinterlegt ist: Erdbeeren auf der Stellage, und im Tunnel nur dort, wo der Betrieb ' +
      'das so festgelegt hat. Freiland und andere Kulturen bekommen kein Substrat.',
    'Laufmeter = Fläche ÷ Reihenabstand. Substrat in m³ = Laufmeter × Liter je Laufmeter ÷ 1.000. ' +
      'Der Richtwert ist Ludgers: 120 m³ je Hektar bei 6.250 Laufmetern, also 19,2 Liter je Laufmeter.',
    'Kosten = m³ × Preis je m³ aus der Preisspiegel-Kachel „Kultursubstrat".',
    'Das Substrat ist eine Schätzung, bis der Betrieb seine eigene Menge und seinen Preis bestätigt hat.',
  ),

  // KB §II.3: payroll by cost centre → Akh per field (B). #4172 wave 2 took the picking wage
  // OUT of this line and gave it its own (`pflueck`), which is what the old text promised
  // would happen — so the promise is replaced by what is now true, not left standing.
  loehne: basis(
    'Wie die Löhne gerechnet sind',
    'B',
    'Löhne = Pflegearbeit + Pflanzarbeit + Sonstige Arbeit — jede Lohnstunde dieses Anbaus, ' +
      'die keine Erntestunde ist — und, wo eine Lohnabrechnung vorliegt, die Posten aus ihr (unten).',
    'Je Stunden-Posten: Arbeitsstunden aus der Lohnabrechnung × Stundensatz, so wie sie auf diesen ' +
      'Anbau gebucht sind, je Monat zusammengefasst. Pflegearbeit ist Schnitt, Hacken, Ausdünnen; ' +
      'Pflanzarbeit der Lohn für das Pflanzen, gebucht auf den Anbau, in den gepflanzt wurde.',
    'Sonstige Arbeit ist Arbeit, die auf diesen Anbau gebucht ist, aber weder Pflege noch Pflanzen — ' +
      'etwa Tunnelbau, Reparaturen, Fahren oder Teamleitung.',
    // v35.1.3 release gate (codex): a month with a Lohnabrechnung (HOF-644) can add Posten that
    // are NOT booked hours, each only where it applies — named by the labels their rows print.
    // Two of them are spread by picked kilos, the one exception to the layer note below.
    'Aus der Lohnabrechnung können Posten dazukommen, die keine gebuchten Stunden sind — jeder nur, ' +
      'wo er für diesen Anbau anfällt: „Lohnnebenkosten (Arbeitgeber)" auf die Löhne darüber — oder, ' +
      'wo die Lohnabrechnung Stundensätze je Personengruppe liefert, „Arbeitgeberkosten laut ' +
      'Lohnabrechnung": Stunden × Durchschnitt je Stunde, abzüglich des gebuchten Lohns —, ' +
      '„Umlage Arbeit ohne Feld" (Fahren, Teamleitung ohne Feldbezug) und „Fester Kulturanteil" der ' +
      'Festangestellten ohne Zeiterfassung. Die Umlage und der Feste Kulturanteil werden nach den ' +
      'gepflückten Kilogramm verteilt — die Ausnahme von der Flächenregel unten.',
    'Der Pflücklohn steht nicht hier, sondern in einer eigenen ertragsabhängigen Zeile.',
  ),

  // KB §I.3 + §II.3: John Deere GPS hours × the published Standardsatz. The KB flags the open
  // question (is Personal inside the rate?) — named here rather than quietly assumed away.
  maschinen: basis(
    'Wie die Maschinenkosten gerechnet sind',
    'B',
    'Maschinenstunden je Schlag × veröffentlichter Maschinenstundensatz.',
    'Der Stundensatz ist ein Standardwert, keine eigene Kalkulation deines Betriebs — ob die ' +
      'Personalkosten darin schon enthalten sind, ist noch zu klären.',
  ),

  // KB §II.2 Layer C, template route.
  etablierung: basis(
    'Wie die Etablierungskosten gerechnet sind',
    'C',
    'Anschaffungswert ÷ Nutzungsdauer, plus Zinsansatz auf das halbe Kapital und Reparatur + ' +
      'Versicherung auf den vollen Wert.',
    'Der Zinsansatz läuft auf die Hälfte, weil über die Nutzungsdauer im Schnitt nur das halbe ' +
      'Kapital gebunden ist — so rechnet die landwirtschaftliche Beratung, und so ist es hier übernommen.',
  ),

  // Layer C „Investition" (2026-10-06) — the real line, in the PRESENT tense; `pending:afa`
  // below keeps the future tense for a fact without it. Two valuations per class, both named:
  // Ludger's kalkulatorischer Stellage standard, and a booked BWA amount split over only the
  // cycles that carry the installation. Unsplit farm pools are shown, never charged.
  investition: basis(
    'Wie die Investition gerechnet ist',
    'C',
    'Einmalige Anschaffungen wie Folientunnel, Stellagen oder Bewässerung werden nicht im Kaufjahr ' +
      'voll angesetzt. Jedes Jahr ihrer Nutzungsdauer trägt einen Teil: AfA, Zinsen, Reparatur und ' +
      'Versicherung.',
    'Jede Anlagenklasse wird auf eine von zwei Arten bewertet, und der Rechenweg nennt, welche.',
    'Kalkulatorisch, nach Ludgers Stellagen-Standard: AfA = Investition ÷ Nutzungsdauer. ' +
      'Zinsansatz = Zinssatz × halbe Investition, weil über die Nutzungsdauer im Schnitt nur das ' +
      'halbe Kapital gebunden ist. Reparatur + Versicherung = Satz × Investition. Die Summe gilt je ' +
      'Hektar (bei manchen Klassen je laufendem Meter oder je Pflanze) und wird mit der Fläche ' +
      'dieses Anbaus multipliziert.',
    'Gebucht: ein Betrag des ganzen Betriebs aus der BWA, zum Beispiel die Reparaturen an der ' +
      'Bewässerung. Er wird nur auf die Anbauten verteilt, die die Anlage tragen — der Tunnel nur auf ' +
      'Tunnelkulturen, die Bewässerung nur auf bewässerte Kulturen. Betrag × Fläche dieses Anbaus ÷ ' +
      'Fläche aller Anbauten, die ihn tragen.',
    'Beträge, die sich noch keiner Kultur zuordnen lassen, stehen unter „nicht verteilt". Sie sind ' +
      'nur zur Einordnung aufgeführt und stecken nicht in diesem Deckungsbeitrag.',
    'Die Zahlen sind Schätzwerte, bis der Betrieb seine eigenen Investitionen und Nutzungsdauern ' +
      'bestätigt hat.',
  ),

  // KB §II.2 D2 + §II.3 (pool: Σ pool ÷ Σ kg). HOF-506 is the implementation of exactly this.
  gemeinkosten: basis(
    'Wie die Gemeinkosten gerechnet sind',
    'D',
    'Summe der Kosten eines Absatz-Pools ÷ Summe der Kilos, die durch diesen Pool gegangen sind.',
    'Ein Pool ist die Hütte oder das Verkaufsbüro, in dem die Kosten anfallen. Nur Pools mit ' +
      'Absatzcharakter verteilen — ein Betriebszweig verteilt nichts.',
  ),

  // HOF-518 §B — the line is real now, so it is described in the PRESENT tense; the
  // `pending:land` block below stays, in the future tense, for every tenant whose curated
  // books have not been published. Both are true at once and neither replaces the other,
  // which is exactly the rule this module's header states about a pending line's two texts.
  land: basis(
    'Wie die Landkosten gerechnet sind',
    'B',
    'Fläche × Pachtansatz je Hektar aus dem Preisspiegel.',
    'Angesetzt wird auf JEDEN Hektar, auch auf die eigenen — sonst sieht eine eigene Fläche ' +
      'billiger aus als eine identische gepachtete, und der Deckungsbeitrag misst die ' +
      'Eigentumsverhältnisse statt der Kultur.',
    'Es liegt noch keine Aufteilung je Schlag vor, deshalb trägt jeder Hektar denselben Satz. ' +
      'Liegt GENAU EIN Pachtvertrag im Archiv, rechnet die Zeile ab der nächsten Neuberechnung ' +
      'mit dessen Pachtzins statt mit dem Schätzwert. Bei mehreren Verträgen bleibt es beim ' +
      'Schätzwert — welcher Satz für welchen Hektar gilt, geht aus den Verträgen noch nicht hervor.',
  ),

  // ── #4172 wave 2: the labour lines, real now, in the PRESENT tense ───────────────
  // Each keeps its `pending:` twin below in the future tense, for a tenant whose payroll
  // does not reach the cycle — the rule this module's header states about a pending line's
  // two texts, applied to every labour line.

  // KB §II.3 layer A. Wave 1 put each wage euro on its own payroll row, carrying an
  // activity class, so this is now MEASURED rather than derived from a rate.
  pflueck: basis(
    'Wie der Pflücklohn gerechnet ist',
    'A',
    'Der Erntelohn aus der Lohnabrechnung, so wie er auf diesen Anbau gebucht ist — Summe der ' +
      'Pflückstunden bzw. des Akkordlohns, nicht auf die Kulturen verteilt und nicht geschätzt.',
    'Die Pflückdaten liegen je Mitarbeiter, Schlag und Tag vor; die Zeile zeigt sie je Monat ' +
      'zusammengefasst.',
    'Ist für den Betrieb eine Erntehelferversicherung oder ein Arbeitgeberzuschlag hinterlegt, ' +
      'steht er als eigener Posten in dieser Zeile — gemessen je Person und Tag, nicht als ' +
      'Prozentsatz auf die Lohnsumme.',
    // HOF-705 — decision B1.
    'Liefert die Lohnabrechnung Stundensätze je Personengruppe, steht der Rest der Arbeitgeberkosten ' +
      'als „Arbeitgeberkosten laut Lohnabrechnung" darunter: die Pflückstunden × der Durchschnitt je ' +
      'Stunde der Personengruppe, abzüglich des gebuchten Lohns.',
  ),

  // KB §II.3 layer A. The pool is monthly and farm-wide, so this is the one labour line whose
  // € is a SHARE rather than a booking on the cycle — and it says so. 2026-10-06 adds the
  // material (worker/ARCH.md § „The Verpackung line carries its material" and § „Two gaps the
  // first cut left"): counted off the delivery lines, priced off the farm's own invoices.
  verpackung: basis(
    'Wie die Verpackung gerechnet ist',
    'A',
    'Verpackung = Lohn der Packhalle + Material (Schalen, Kisten und die Gebühr für Mehrwegsteigen).',
    'Lohn: die Lohnsumme der Packhalle eines Monats × dem Anteil, den dieser Anbau an den in diesem ' +
      'Monat gepflückten Kilos hatte.',
    'Es ist ein Anteil, keine Buchung auf diesen Anbau: in der Packhalle wird nicht je Kultur ' +
      'gestempelt, also verteilen die gepflückten Kilos die Summe. Monate ohne Pflückmenge ' +
      'verteilen nichts — die Summe bleibt dann ausgewiesen, statt gleichmäßig verschmiert zu werden.',
    'Material wird gezählt, nicht pauschal angesetzt. Schalen = Gebinde × Schalen je Gebinde, so wie ' +
      'es auf der Lieferscheinzeile steht (z. B. „10 x 500g"). Kisten und Mehrwegsteigen kommen aus den ' +
      'Kisten- und Pfandzeilen desselben Lieferscheins; zurückgegebene Steigen werden abgezogen.',
    'Der Stückpreis kommt aus deinen Eingangsrechnungen: Rechnungsbetrag ÷ Stückzahl, gewichtet über ' +
      'die Einkäufe der Saison. Nennt eine Rechnung einen Preis je 100 oder je 1.000 Stück, wird die ' +
      'Stückzahl aus Betrag und Preis zurückgerechnet. Gibt es in der Saison keinen Einkauf, gilt der ' +
      'Preis aus dem letzten Vorjahr.',
    'Nennt die Rechnung für eine Schale keine Füllmenge, sondern nur Maße, gilt die Füllmenge, die du ' +
      'für diesen Lieferantenartikel hinterlegt hast. Ein Stück ohne Rechnungspreis steht mit 0,00 € ' +
      'in der Zeile — ein Preis wird nicht geschätzt.',
    'Für Mehrwegsteigen zählt die Gebühr des Poolbetreibers, nicht das Pfand.',
    'Auch das Material ist ein Anteil nach den gepflückten Kilos des Monats: kein Lieferschein weiß, ' +
      'von welchem Schlag die Ware kam.',
  ),

  kuehlung: basis(
    'Wie Kühlung und Pfandkisten gerechnet sind',
    'A',
    'Gepflückte Kilogramm × einem Satz je Kilo für Kühlung und Pfandkisten-Nutzung.',
    'Der Satz ist ein Referenzwert aus der Beratungsliteratur, solange der Betrieb keinen eigenen ' +
      'bestätigt hat — die Zeile ist dann mit „geschätzt" gekennzeichnet.',
    'Das Pfand selbst ist kein Kostenblock: es wird an den Lieferanten gezahlt und dem Kunden ' +
      'weiterberechnet, also hebt es sich auf. Nur die Nutzung der Kisten ist eine echte Kostenposition.',
  ),

  // ── the pending lines: what WILL stand there, in the future tense ────────────────
  'pending:pflueck': basis(
    'Wie der Pflücklohn gerechnet wird',
    'A',
    'Der Erntelohn aus der Lohnabrechnung, gebucht auf den Anbau, auf dem gepflückt wurde.',
    'Für diesen Anbau liegt keine Lohnabrechnung vor. Sobald sie eingelesen ist, steht hier der ' +
      'gemessene Betrag — er wird nicht aus einer Pflückleistung hochgerechnet.',
  ),

  'pending:verpackung': basis(
    'Wie die Verpackung gerechnet wird',
    'A',
    'Die Lohnsumme der Packhalle eines Monats, verteilt nach den in diesem Monat gepflückten Kilos.',
    'Für diesen Anbau ist in der Packhalle noch keine Lohnsumme angefallen oder keine Pflückmenge ' +
      'erfasst, an der sie hängen könnte. Das Material (Schalen, Steigen) kommt dazu, sobald für die ' +
      'Kultur Lieferscheine mit Gebinden und die Rechnungen der Verpackungslieferanten vorliegen.',
  ),

  'pending:kuehlung': basis(
    'Wie Kühlung und Pfandkisten gerechnet werden',
    'A',
    'Gepflückte Kilogramm × einem Satz je Kilo für Kühlung und Pfandkisten-Nutzung.',
    'Für diesen Anbau ist keine Pflückmenge in Kilogramm erfasst, oder für die Kultur ist kein Satz ' +
      'hinterlegt — ohne beides bleibt die Zeile leer statt 0,00 € zu behaupten.',
    'Das Pfand selbst ist kein Kostenblock: es wird an den Lieferanten gezahlt und dem Kunden ' +
      'weiterberechnet, also hebt es sich auf. Nur die Nutzung der Kisten ist eine echte Kostenposition.',
  ),

  'pending:land': basis(
    'Wie die Landkosten gerechnet werden',
    'B',
    'Fläche × Pachtansatz je Hektar.',
    'Angesetzt wird auf JEDEN Hektar, auch auf die eigenen — sonst sieht eine eigene Fläche ' +
      'billiger aus als eine identische gepachtete, und der Deckungsbeitrag misst die ' +
      'Eigentumsverhältnisse statt der Kultur.',
    'Wie genau verteilt wird, ist noch offen: heute liegt nur eine Gesamtsumme ohne Aufteilung je ' +
      'Schlag vor, der Zwischenstand ist deshalb ein flacher Betrag je Hektar.',
  ),

  // Layer C — the future tense of the `investition` block above, for a fact without the line.
  'pending:afa': basis(
    'Wie AfA, Zinsen, Reparatur und Versicherung gerechnet werden',
    'C',
    'Je Anlagenklasse (Folientunnel, Stellage, Bewässerung …) entweder kalkulatorisch — Investition ÷ ' +
      'Nutzungsdauer, plus Zinsansatz auf das halbe Kapital, plus Reparatur und Versicherung als Satz ' +
      'auf die Investition, je Hektar × Fläche — oder als gebuchter Betrag aus der BWA, verteilt nur ' +
      'auf die Anbauten, die die Anlage tragen, nach ihrer Fläche.',
    'Sobald der Deckungsbeitrag neu berechnet ist und für den Betrieb Anlagenklassen hinterlegt sind, ' +
      'steht hier der Betrag je Klasse.',
  ),

  'pending:absatz': basis(
    'Wie die Absatzkosten gerechnet werden',
    'D',
    'Auslieferung je Kilo einer Tour + Provision und Kartengebühren als Prozentsatz vom Preis des ' +
      'jeweiligen Geschäfts.',
    'Gerechnet wird je Geschäft und erst danach auf Abnehmer und Kanal hochsummiert — ein Kanal hat ' +
      'keine eigenen Kosten, er ist eine Summe über die Geschäfte, die ihm zugeordnet sind.',
  ),
};

/**
 * The line that keeps a cost block honest about what it is (codex r1 P1).
 *
 * These texts describe the AGREED MODEL — what the line is and how it is meant to travel
 * from document to €/kg — and that is deliberate: describing what the code happens to do
 * today would document an implementation and drift the moment a line's source changed. But
 * the Rechenweg directly above them shows what this cycle actually had, and the two can
 * visibly disagree: a `duenger_psm` line derived from a booked invoice total sits under
 * „Ausgebrachte Menge … × Preis … aus dem Preisspiegel", and a machine line taken from an
 * operation's own `cost` sits under „Maschinenstunden × Standardsatz". Left unsaid, that is
 * a contradiction on one screen and the reader has no way to tell which half to believe.
 *
 * So the block says which of the two it is. One sentence, not a per-source matrix: the
 * matrix would have to be re-derived every time a builder changes, and it would answer a
 * question („how was THIS € computed") the Rechenweg above already answers better.
 *
 * The Leistung is excluded because its text is not a model description — „Erfasste Menge ×
 * gewichteter Monatsdurchschnittspreis" is exactly what the materialiser does, and pointing
 * at the Rechenweg for something better would be false modesty.
 */
export const MODEL_SCOPE_NOTE =
  'So ist die Zeile im vereinbarten Kostenmodell definiert — was für diesen Schlag tatsächlich ' +
  'an Buchungen vorliegt, steht im Rechenweg darüber.';

/** Whether `lineKey`'s block describes the model rather than this fact's own computation. */
export function describesTheModel(lineKey: string | null | undefined): boolean {
  return lineBasisFor(lineKey) !== null && lineKey !== 'leistung';
}

/**
 * The block for a selected line, or `null` when nothing is selected or the key carries no
 * description.
 *
 * `null` rather than a generic fallback on purpose: a line this map does not know is a line
 * whose basis nobody has written down, and inventing a plausible sentence for it is worse
 * than saying nothing — it is the one failure mode this whole block exists to remove.
 */
export function lineBasisFor(lineKey: string | null | undefined): LineBasis | null {
  if (!lineKey) return null;
  return LINE_BASIS[lineKey] ?? null;
}
